import { Modal } from "@stormwater-software/lib-ui";
import { asSchemaDefinition, SchemaFieldsWithContext } from '@stormwater-software/lib-form';
import { UIControlType } from '@stormwater-software/lib-form-control-types';
import React, { useState, useEffect } from "react";
import FormActions from '../form/modal/FormActions';

export interface IMFAVerificationModalProps {
  show: boolean;
  handleClose: () => void;
  onVerificationSuccess: () => void;
  email: string;
  sendVerificationCode: (email: string, method: string) => Promise<any>;
  verifyCode: (email: string, code: string, rememberDevice: boolean, method: string) => Promise<any>;
}

const MFAVerificationModal: React.FC<IMFAVerificationModalProps> = ({
  show,
  handleClose,
  onVerificationSuccess,
  email,
  sendVerificationCode,
  verifyCode,
}) => {
  const [step, setStep] = useState<'method' | 'verify'>(show ? 'method' : 'method');
  const [selectedVerificationMethod, setSelectedVerificationMethod] = useState<'sms' | 'email'>('email');
  const [identifier, setIdentifier] = useState<string | null>(null);
  const [resendTimer, setResendTimer] = useState(0);


  
  // Reset state when modal is opened
  useEffect(() => {
    if (show) {
      setStep('method');
      setSelectedVerificationMethod('email');
      setIdentifier(null);
      setResendTimer(0);
    }
  }, [show]);

  // Timer effect for resend code countdown
  useEffect(() => {
    let interval: NodeJS.Timeout | null = null;
    
    if (resendTimer > 0) {
      interval = setInterval(() => {
        setResendTimer((prev) => prev - 1);
      }, 1000);
    }
    
    return () => {
      if (interval) {
        clearInterval(interval);
      }
    };
  }, [resendTimer]);

  const schemaMethod = asSchemaDefinition({
    verificationMethod: {
      controlType: UIControlType.radio,
      validation: { required: true },
      options: [
        {
          label: `Email`,
          value: 'email',
        },
        {
          label: 'Text Message',
          value: 'sms',
        },
      ],  
      className: 'verification-method-radio',
    },
  });

  const schemaVerify = asSchemaDefinition({
    verificationCode: {
      label: 'Verification Code',
      className: 'bottom-outline',
      placeholder: 'Enter 6-digit code',
      validation: {
        required: true,
        maxLength: 6
      },
      autoComplete: 'off',
    },
    rememberDevice: {
      label: 'Remember this device for 14 days',
      controlType: UIControlType.checkbox,
      className: 'remember-device-checkbox',
    },
  });

  const handleSendVerificationCode = async ({ verificationMethod: method }: { verificationMethod: string }) => {
    try {
      setSelectedVerificationMethod(method as 'sms' | 'email');
      
      const result = await sendVerificationCode(email, method);
      const identity = result?.identifier;

      setIdentifier(identity || null);

      // Start the 60-second timer
      setResendTimer(60);
      
      // Move to code verification step
      setStep('verify');
    } catch (error) {
      console.error('Error sending verification code:', error);
      throw error;
    }
  };

  const handleVerifyCode = async ({ verificationCode: code, rememberDevice }: { verificationCode: string; rememberDevice?: boolean }) => {
    try {
      const verificationResult = await verifyCode(email, code, rememberDevice || false, selectedVerificationMethod);
      
      if (!verificationResult) {
        throw new Error('Invalid verification code');
      }

      // If verification successful, call success callback
      onVerificationSuccess();
      handleClose();
    } catch (error) {
      console.error('Error verifying code:', error);
      throw error;
    }
  };

  const handleResendCode = async () => {
    if (resendTimer > 0) return; // Prevent resend if timer is still active
    
    try {
      await sendVerificationCode(email, selectedVerificationMethod);

      // Restart the 60-second timer
      setResendTimer(60);
    } catch (error) {
      console.error('Error resending verification code:', error);
    }
  };

  const handleModalClose = () => {
    setStep('method');
    setResendTimer(0);
    handleClose();
  };


  return (
    <Modal
      title={step === 'method' ? 'Multi-Factor Authentication (MFA)' : 'Enter Verification Code'}
      show={show}
      hideButtons
      handleClose={handleModalClose}
    >
      <div className="mfa-verification-modal-content">
        {step === 'method' ? (
          <>
            <p>For security, your organization requires an additional verification step. Please complete the multi-factor authentication to continue.</p>
            <p><strong>How would you like to receive your verification code?</strong></p>
            <SchemaFieldsWithContext className='mfa-method-form' schema={schemaMethod}>
              <FormActions
                submitText='Send Code'
                onSubmit={handleSendVerificationCode}
                onCancel={handleModalClose}
                showCancel={true}
                small={false}
                showSubmit={true}
                showErrorText={true}
                errorMessageText='Please select a verification method.'
                cancelText='Cancel'
              />
            </SchemaFieldsWithContext>
          </>
        ) : (
          <>
            <p>
              Code sent by {selectedVerificationMethod === 'email' ? 'email' : 'text message'} to {identifier}
            </p>
            <SchemaFieldsWithContext className='mfa-verify-form' schema={schemaVerify}>
              <div className='resend-code-section'>
                <p>Didn't receive the code?</p>
                <button
                  type='button'
                  className='btn-link resend-code-button'
                  onClick={handleResendCode}
                  disabled={resendTimer > 0}
                  style={{ 
                    cursor: resendTimer > 0 ? 'not-allowed' : 'pointer',
                    opacity: resendTimer > 0 ? 0.5 : 1
                  }}
                >
                  <span className='resend-icon'>&#x21bb;</span> 
                  {resendTimer > 0 ? ` Resend Code (${resendTimer}s)` : ' Resend Code'}
                </button>
              </div>
              
              <FormActions
                submitText='Verify Code'
                onSubmit={handleVerifyCode}
                onCancel={() => setStep('method')}
                showCancel={true}
                small={false}
                showSubmit={true}
                showErrorText={true}
                errorMessageText='Invalid verification code. Please try again.'
                cancelText='Back'
              />
            </SchemaFieldsWithContext>
          </>
        )}
      </div>
    </Modal>
  );
};

export default MFAVerificationModal;
